import React, { useState, useEffect, useRef } from 'react';
import { ExternalLink, ChevronRight, X, ChevronLeft, ChevronRight as ChevronRightIcon } from 'lucide-react';
import useScrollReveal from '../hooks/useScrollReveal';
import './Portfolio.css';

const projects = [
  {
    tag: 'Customer Experience Platform',
    title: 'VibeWall',
    desc: 'A branded customer experience platform that transforms a simple QR code into an interactive digital hub. Customers can browse menus, upload photos, discover community posts, participate in campaigns, and place orders — all from one seamless mobile experience without downloading an app. Designed for cafés, restaurants, and lifestyle businesses.',
    color: '#A855F7',
    bg: 'linear-gradient(135deg, #1a0a2e, #2d1054)',
    accent: '#F472B6',
    saas: true,
    flagship: true,
    images: ['/Vibewall/vw_posting1.jpg', '/Vibewall/vw_posting2.jpg', '/Vibewall/vw_posting3.jpg', '/Vibewall/vw_posting4.jpg'],
    demoLink: 'https://vibewall-83s.pages.dev/',
  },
  {
    tag: 'RFID Technology',
    title: 'ClockedIn: RFID DTR Attendance Monitoring System',
    desc: 'RFID-based attendance tracking system with automated time logs, monitoring, and reporting features.',
    color: '#F59E0B',
    bg: 'linear-gradient(135deg, #1a1000, #2a1a00)',
    accent: '#FCD34D',
    product: true,
    images: ['/ClockedIn/Main_Interface.png', '/ClockedIn/ESS_Home_Page.png', '/ClockedIn/ESS_Attendance_Page.png'],
  },
  {
    tag: 'Facilities Reservation System',
    title: 'Facilities Reservation Management',
    desc: 'A smart reservation platform for managing facility bookings, schedules, approvals, and slot availability.',
    color: '#6C3EF6',
    bg: 'linear-gradient(135deg, #0f0f2a, #1a1a4e)',
    accent: '#22D3EE',
    images: ['/ViaHale/Login.png', '/ViaHale/End_Users_Landing_Page.png', '/ViaHale/Requests.png'],
  },
  {
    tag: 'Security & Monitoring',
    title: 'Visitor Management System',
    desc: 'QR-powered visitor registration and monitoring solution with real-time check-in and approval workflows.',
    color: '#22D3EE',
    bg: 'linear-gradient(135deg, #0a1628, #0d2040)',
    accent: '#6C3EF6',
    images: ['/ViaHale/visitor.png', '/ViaHale/visitor1.png'],
  },
  {
    tag: 'Business Management',
    title: 'Inventory, Sales, and Cost Management System',
    desc: 'Inventory tracking platform for managing stocks, supplies, product monitoring, and warehouse operations. Business analytics and sales monitoring system with expense tracking, reporting, and financial insights.',
    color: '#EC4899',
    bg: 'linear-gradient(135deg, #2a1020, #3b1230)',
    accent: '#F472B6',
    images: ['/MyStore/Screenshot 2026-05-11 114339.png', '/MyStore/Screenshot 2026-05-11 114347.png', '/MyStore/Screenshot 2026-05-11 114354.png'],
  },
  {
    tag: 'Document Solution',
    title: 'Document Management',
    desc: 'Centralized document storage system with folder organization, release workflows, and access control.',
    color: '#10B981',
    bg: 'linear-gradient(135deg, #081a14, #0d2a1f)',
    accent: '#34D399',
    images: ['/ViaHale/Admin_Document.png'],
  },
  {
    tag: 'Anime Collectibles Marketplace',
    title: 'Swabe',
    desc: "Discover premium anime collectibles from the world's most iconic series. Swabe brings together carefully curated figures, statues, and exclusive pieces in one beautifully organized marketplace, making it easy for collectors to browse by universe, explore new releases, and find their next prized addition.",
    color: '#EF4444',
    bg: 'linear-gradient(135deg, #1a0808, #2d1010)',
    accent: '#FB923C',
    images: ['/Swabe/swabe1.png', '/Swabe/swabe2.png'],
  },
];

export default function Portfolio() {
  const [modal, setModal] = useState(null);
  const [closing, setClosing] = useState(false);
  const closeTimer = useRef(null);
  useScrollReveal();

  const openModal = (projectIndex) => {
    setClosing(false);
    setModal({ projectIndex, imageIndex: 0 });
  };

  const closeModal = () => {
    setClosing(true);
    closeTimer.current = setTimeout(() => {
      setModal(null);
      setClosing(false);
    }, 220);
  };

  const prevImage = (e) => {
    e?.stopPropagation();
    setModal((m) => ({
      ...m,
      imageIndex: (m.imageIndex - 1 + projects[m.projectIndex].images.length) % projects[m.projectIndex].images.length,
    }));
  };

  const nextImage = (e) => {
    e?.stopPropagation();
    setModal((m) => ({
      ...m,
      imageIndex: (m.imageIndex + 1) % projects[m.projectIndex].images.length,
    }));
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (!modal) return;
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [modal]);

  useEffect(() => {
    document.body.style.overflow = modal ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [modal]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const activeProject = modal !== null ? projects[modal.projectIndex] : null;

  const getSlideIndex = (offset) => {
    if (!activeProject) return 0;
    const len = activeProject.images.length;
    return (modal.imageIndex + offset + len) % len;
  };

  return (
    <section className="portfolio" id="portfolio">
      <div className="container">
        <div className="section-label">PROJECTS SHOWCASE</div>
        <h2 className="section-title">Projects Delivered</h2>
        <p className="section-sub">
          Explore some of our recent work across different industries and technology stacks.
        </p>

        <div className="portfolio-list">
          {projects.map((p, i) => (
            <button
              key={i}
              className={`project-row reveal-card${p.flagship ? ' project-row-flagship' : ''}`}
              onClick={() => openModal(i)}
              style={{ '--row-color': p.color, animationDelay: `${i * 0.07}s` }}
            >
              <div className="project-row-bar" style={{ background: p.color }} />

              <div className="project-thumb" style={{ background: p.bg }}>
                <img
                  src={encodeURI(p.images[0])}
                  alt={p.title}
                  className="project-thumb-img"
                />
              </div>

              <div className="project-row-body">
                <div className="project-row-top">
                  <div className="project-row-tag" style={{ color: p.color }}>{p.tag}</div>
                  {p.saas && <span className="saas-badge">SaaS</span>}
                  {p.product && <span className="product-badge">Product</span>}
                </div>
                <div className="project-row-title">{p.title}</div>
                <div className="project-row-desc">{p.desc}</div>
              </div>

              <ChevronRight size={18} className="project-row-arrow" style={{ color: p.color }} />
            </button>
          ))}
        </div>
      </div>

      {/* ── Cinematic Modal ── */}
      {modal !== null && activeProject && (
        <div
          className={`pm-overlay${closing ? ' pm-closing' : ''}`}
          onClick={closeModal}
          style={{ '--pm-color': activeProject.color }}
        >
          {/* Atmosphere layers */}
          <div className="pm-bg-base" />
          <div className="pm-bg-color" />

          <div
            className={`pm-box${closing ? ' pm-box-closing' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="pm-header">
              <div>
                <div className="pm-modal-tag-row">
                  <div className="pm-modal-tag" style={{ color: activeProject.color }}>
                    {activeProject.tag}
                  </div>
                  {activeProject.saas && <span className="saas-badge">SaaS</span>}
                  {activeProject.product && <span className="product-badge">Product</span>}
                </div>
                <div className="pm-modal-title">{activeProject.title}</div>
              </div>
              <button className="pm-close" onClick={closeModal} aria-label="Close">
                <X size={18} />
              </button>
            </div>

            {/* Carousel */}
            <div className="pm-image-wrap">
              {activeProject.images.length === 1 ? (
                /* Single image — full width */
                <div className="pm-slide pm-slide-solo">
                  <img
                    src={encodeURI(activeProject.images[0])}
                    alt={`${activeProject.title} screenshot`}
                  />
                </div>
              ) : (
                /* Multi-image — cinematic 3-up */
                <>
                  <div className="pm-carousel-track">
                    {/* Prev */}
                    <div
                      className="pm-slide pm-slide-side"
                      onClick={(e) => { e.stopPropagation(); prevImage(); }}
                      aria-label="Previous image"
                      role="button"
                    >
                      <img
                        src={encodeURI(activeProject.images[getSlideIndex(-1)])}
                        alt=""
                        aria-hidden="true"
                      />
                    </div>

                    {/* Current */}
                    <div className="pm-slide pm-slide-main">
                      <img
                        src={encodeURI(activeProject.images[modal.imageIndex])}
                        alt={`${activeProject.title} screenshot ${modal.imageIndex + 1}`}
                      />
                    </div>

                    {/* Next */}
                    <div
                      className="pm-slide pm-slide-side"
                      onClick={(e) => { e.stopPropagation(); nextImage(); }}
                      aria-label="Next image"
                      role="button"
                    >
                      <img
                        src={encodeURI(activeProject.images[getSlideIndex(1)])}
                        alt=""
                        aria-hidden="true"
                      />
                    </div>
                  </div>

                  {/* Arrow buttons */}
                  <button
                    className="pm-nav pm-nav-prev"
                    onClick={(e) => { e.stopPropagation(); prevImage(); }}
                    aria-label="Previous"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    className="pm-nav pm-nav-next"
                    onClick={(e) => { e.stopPropagation(); nextImage(); }}
                    aria-label="Next"
                  >
                    <ChevronRightIcon size={18} />
                  </button>

                  {/* Counter */}
                  <div className="pm-counter">
                    {modal.imageIndex + 1} / {activeProject.images.length}
                  </div>
                </>
              )}
            </div>

            {/* Dots */}
            {activeProject.images.length > 1 && (
              <div className="pm-dots">
                {activeProject.images.map((_, di) => (
                  <button
                    key={di}
                    className={`pm-dot${di === modal.imageIndex ? ' pm-dot-active' : ''}`}
                    style={di === modal.imageIndex ? { background: activeProject.color } : {}}
                    onClick={(e) => { e.stopPropagation(); setModal((m) => ({ ...m, imageIndex: di })); }}
                    aria-label={`Image ${di + 1}`}
                  />
                ))}
              </div>
            )}

            {/* Footer */}
            <div className="pm-footer">
              <p className="pm-desc">{activeProject.desc}</p>
              <a
                href={activeProject.demoLink || '#contact'}
                target={activeProject.demoLink ? '_blank' : undefined}
                rel={activeProject.demoLink ? 'noopener noreferrer' : undefined}
                className="pm-cta"
                style={{ background: activeProject.color }}
                onClick={closeModal}
              >
                Request for Demo <ExternalLink size={14} />
              </a>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}